// frontend/src/components/PortConditionsPanel.jsx
import { useCallback, useEffect, useState } from 'react';
import api from '../services/api.js';

/** Dispatcher: today's weather / sea / shore remarks per port, and tugs still to report. */
export default function PortConditionsPanel() {
  const [ports, setPorts] = useState([]);
  const [missing, setMissing] = useState([]);
  const [savedPort, setSavedPort] = useState('');
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    try {
      const [c, m] = await Promise.all([api.getPortConditions(), api.getDtrMissing()]);
      setPorts(c.ports);
      setMissing(m);
    } catch (e) {
      setError(e.message || 'Failed to load conditions.');
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const edit = (portId, patch) => setPorts((ps) => ps.map((p) => (p.portId === portId ? { ...p, ...patch } : p)));
  const save = async (p) => {
    setError('');
    try {
      await api.savePortConditions(p.portId, { weather: p.weather || null, seaCondition: p.seaCondition || null, shoreRemarks: p.shoreRemarks || null });
      setSavedPort(p.portId);
    } catch (e) {
      setError(e.message || 'Failed to save conditions.');
    }
  };

  return (
    <div className="card border-0 shadow-sm mb-3" data-testid="conditions-panel">
      <div className="card-header bg-white pt-3 border-bottom-0">
        <h6 className="mb-0 fw-bold text-uppercase small text-secondary">Today&apos;s conditions &amp; missing reports</h6>
      </div>
      <div className="card-body">
        {error && <div className="alert alert-danger py-2">{error}</div>}
        {ports.map((p) => (
          <div className="row g-2 align-items-end mb-2" key={p.portId}>
            <div className="col-md-2 fw-bold small">{p.portName}</div>
            <div className="col-md-2"><input className="form-control form-control-sm" placeholder="Weather" aria-label={`Weather at ${p.portName}`} value={p.weather ?? ''} onChange={(e) => edit(p.portId, { weather: e.target.value })} data-testid={`conditions-weather-${p.portId}`} /></div>
            <div className="col-md-2"><input className="form-control form-control-sm" placeholder="Sea condition" aria-label={`Sea condition at ${p.portName}`} value={p.seaCondition ?? ''} onChange={(e) => edit(p.portId, { seaCondition: e.target.value })} data-testid={`conditions-sea-${p.portId}`} /></div>
            <div className="col-md-4"><input className="form-control form-control-sm" placeholder="Shore remarks" aria-label={`Shore remarks for ${p.portName}`} value={p.shoreRemarks ?? ''} onChange={(e) => edit(p.portId, { shoreRemarks: e.target.value })} data-testid={`conditions-remarks-${p.portId}`} /></div>
            <div className="col-md-2">
              <button className="btn btn-sm btn-outline-primary" onClick={() => save(p)} data-testid={`conditions-save-${p.portId}`}>Save</button>
              {savedPort === p.portId && <span className="text-success small ms-2" data-testid="conditions-saved">Saved</span>}
            </div>
          </div>
        ))}
        <hr />
        <div data-testid="missing-reports">
          {missing.length === 0 ? (
            <span className="text-success small">All tugs have reported.</span>
          ) : (
            <details open={missing.length <= 10}>
              <summary className="small fw-bold text-danger" data-testid="missing-summary">
                {missing.length} daily tug report{missing.length === 1 ? '' : 's'} still missing
              </summary>
              <ul className="list-unstyled small mb-0 mt-2">
                {missing.map((m) => (
                  <li key={`${m.date}-${m.tugId}`} data-testid="missing-row" data-tug={m.tugName} data-date={m.date}>
                    <span className="text-muted">{m.date}</span> · <span className="fw-bold">{m.tugName}</span> — {m.missing.join(' and ')} section missing
                  </li>
                ))}
              </ul>
            </details>
          )}
        </div>
      </div>
    </div>
  );
}
